/**
 * Breakdown Readiness Calculator
 *
 * Determines whether the user has confirmed/reviewed enough fields
 * to proceed to canonicalization and phase1-start.
 */

import type { ExtractionFieldData } from '@/components/intake/ExtractionField';

const HARD_REQUIRED_FIELDS = ['optionA', 'optionB', 'decisionType', 'impactScore', 'urgencyScore'];

export interface BreakdownReadiness {
  hardFieldsComplete: boolean;
  canProceedToCanonicalization: boolean;
  unresolvedCriticalCount: number;
  unresolvedMissingCount: number;
  fieldReadiness: Record<string, boolean>;
}

function isFieldReviewed(field: ExtractionFieldData | undefined): boolean {
  if (!field) return false;
  if (field.detection_status === 'missing') return false;
  const cs = field.confirmation_status;
  return cs === 'accepted' || cs === 'edited' || cs === 'confirmed';
}

export function computeBreakdownReadiness(
  fields: Record<string, ExtractionFieldData>,
  missingFields: string[],
  conflicts: Array<{ field_name: string }>,
): BreakdownReadiness {
  const fieldReadiness: Record<string, boolean> = {};

  for (const fieldName of HARD_REQUIRED_FIELDS) {
    fieldReadiness[fieldName] = isFieldReviewed(fields[fieldName]);
  }

  const hardFieldsComplete = HARD_REQUIRED_FIELDS.every(f => fieldReadiness[f]);

  // Check for unresolved conflicts on hard-required fields
  const hardConflicts = conflicts.filter(c => HARD_REQUIRED_FIELDS.includes(c.field_name));

  const unresolvedMissingCount = missingFields.length;
  const unresolvedCriticalCount = HARD_REQUIRED_FIELDS.filter(f => !fieldReadiness[f]).length + hardConflicts.length;

  const canProceedToCanonicalization = hardFieldsComplete && hardConflicts.length === 0;

  return {
    hardFieldsComplete,
    canProceedToCanonicalization,
    unresolvedCriticalCount,
    unresolvedMissingCount,
    fieldReadiness,
  };
}
